/**
 * The platform console's step-up challenge.
 *
 * Same shape as the tenant app's (`web/src/api/stepup.ts`) and for the same
 * reason: `403 ERR_STEP_UP_REQUIRED` (CLAUDE.md §6/§13) is a question, and the
 * only sane place to answer it is the transport, so that every guarded action
 * is covered rather than whichever screen remembered.
 *
 * The console reaches this code today only when a platform route is guarded —
 * `RequireStepUp` is derived from the tenant RBAC catalogue, which the admin
 * realm does not use, so nothing in `/admin/api/v1` emits the refusal yet. The
 * endpoint that answers it (`POST /admin/api/v1/auth/step-up`) does exist, MFA
 * is mandatory in this realm, and an operator meeting a step-up with no way to
 * answer it is the exact failure this work exists to remove. Wiring it now
 * costs a file; wiring it after the first guarded admin route ships costs an
 * incident.
 *
 * Nothing here imports `client.ts`: the client installs the verifier, the
 * dialog installs the enrolment route, and the arrows stay one-way.
 */

import { createStepUpBroker } from '@erpx/ui'

/** The console-wide challenge broker. The dialog renders `stepUp.prompt`. */
export const stepUp = createStepUpBroker()

/**
 * Which guarded action provoked the challenge, as a catalogue key suffix.
 *
 * Support helpers are the operations most likely to be guarded first — §10
 * mints support tokens and resets other people's credentials — so they are
 * named; everything else gets wording that is honest about not knowing.
 */
export type StepUpReason = 'support' | 'billing' | 'tenant' | 'generic'

const ROUTES: readonly { pattern: RegExp; reason: StepUpReason }[] = [
  {
    // `users$` is the collection POST — creating a tenant user, which may mint
    // a password the operator then reads (admin.tenantUsers.create). It is
    // anchored, on a path the caller has already stripped the query string
    // from, so the per-user routes are matched by their own alternative rather
    // than by this one. The GET that lists users matches the same shape; that
    // is harmless, because this function only *names* a challenge the server
    // has already raised, and no reading route ever raises one.
    pattern:
      /\/tenants\/[^/]+\/(support-session|users$|users\/[^/]+\/(set-password|reset-password|reset-mfa|unlock|resend-invite|deactivate|revoke-sessions))/,
    reason: 'support',
  },
  { pattern: /\/support-sessions(\/|$)/, reason: 'support' },
  { pattern: /\/(payments|invoices|subscriptions|plans)(\/|$)/, reason: 'billing' },
  { pattern: /\/tenants(\/|$)/, reason: 'tenant' },
]

export function stepUpReason(path: string): StepUpReason {
  const withoutQuery = path.split('?')[0] ?? path
  for (const route of ROUTES) {
    if (route.pattern.test(withoutQuery)) return route.reason
  }
  return 'generic'
}
